import React, { useEffect, useMemo, useState } from 'react';
import { exportDownload, exportJson, exportPost, exportPostDownload } from './exportsApi';

/* ──────────────────────────────────────────────────────────────────────────
   پنجرهٔ «ایجاد فایل سناما»

   • از سند / تا سند  ← پیش‌فرض کمترین و بیشترین شمارهٔ سند سال
   • «کنترل» و «کنترل اسناد»
   • جستجوی حساب و «تا سند شماره» به‌عنوان شرط فهرست
   • «محاسبه»، گرید سناما با ستون‌های کمبودها/ناهمخوانی ها
   • جمع گردش بدهکار / بستانکار / مانده و تعداد ردیف
   • «تراز نهایی» فقط وقتی تاریخ «تا» در ماه ۱۲ است ⇒ Month = 15
   • «ذخیره فایل» ⇒ ZIP حاوی XML سناما
   • راست‌کلیک روی ردیف ⇒ ریز آرتیکل‌ها؛ راست‌کلیک روی پنل ⇒ بستن
   • «اکسل» ⇒ گرید فعال (اصلی یا جزئیات)
   ────────────────────────────────────────────────────────────────────────── */

const fa = (v) => (v === null || v === undefined ? '' : String(v).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]));
const money = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? fa(Math.abs(n).toLocaleString('en-US')) + (n < 0 ? '-' : '') : '';
};
const MONEY_COLS = new Set(['SummaryProgressDeptor', 'SummaryProgressCreditor', 'DocumentDetailDebit', 'DocumentDetailCredit']);

function Grid({ columns, rows, onRowContext, onRowDouble, selected, onSelect, maxHeight }) {
  return (
    <div style={{ ...st.gridWrap, maxHeight }}>
      <table style={st.table}>
        <thead>
          <tr>
            <th style={st.th}>#</th>
            {columns.map((c) => <th key={c.key} style={st.th}>{c.title}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}
              onClick={() => onSelect && onSelect(i)}
              onDoubleClick={() => onRowDouble && onRowDouble(r, i)}
              onContextMenu={(e) => { if (onRowContext) { e.preventDefault(); onSelect && onSelect(i); onRowContext(r, i); } }}
              style={{ background: selected === i ? '#cfe3ff' : (i % 2 ? '#fafbfc' : '#fff'), cursor: 'default' }}>
              <td style={st.tdC}>{fa(i + 1)}</td>
              {columns.map((c) => {
                const v = r[c.key];
                if (MONEY_COLS.has(c.key)) return <td key={c.key} style={st.tdN}>{money(v)}</td>;
                const warn = c.key === 'کمبودها' || c.key === 'ناهمخوانی ها';
                return (
                  <td key={c.key} style={{ ...st.td, ...(warn ? st.warn : {}) }}>
                    {fa(typeof v === 'string' ? v.replace(/^\n+/, '') : v)}
                  </td>
                );
              })}
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={columns.length + 1} style={st.empty}>اطلاعاتی برای نمایش وجود ندارد</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ControlPreview({ title, rows, onClose }) {
  const print = () => {
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
    const body = rows.map((r) => (r.NewPage === 1
      ? `<tr class="h"><td colspan="3">${esc(r.Remark)}</td></tr>`
      : `<tr><td class="r">${esc(r.Remark)}</td><td class="n">${r.Debit == null ? '' : esc(money(r.Debit))}</td>`
        + `<td class="n">${r.Credit == null ? '' : esc(money(r.Credit))}</td></tr>`)).join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html dir="rtl"><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>body{font-family:Vazir,Vazirmatn,Tahoma;font-size:12px}table{width:100%;border-collapse:collapse}
      td,th{border:1px solid #000;padding:3px 5px}.h td{background:#e8e8e8;font-weight:700;white-space:pre-line}
      .r{white-space:pre-line}.n{text-align:left;direction:ltr;width:130px}h3{text-align:center}</style></head>
      <body><h3>${esc(title)}</h3><table><thead><tr><th>شرح</th><th>بدهکار</th><th>بستانکار</th></tr></thead>
      <tbody>${body}</tbody></table><script>window.print()</script></body></html>`);
    w.document.close();
  };
  return (
    <div style={st.overlay2} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={st.preview}>
        <div style={st.head}>
          <span>{title}</span>
          <span>
            <button onClick={print} style={st.btnSm}>🖨️ چاپ</button>
            <button onClick={onClose} style={st.x}>✕</button>
          </span>
        </div>
        <div style={{ overflow: 'auto', padding: 10 }}>
          <table style={st.table}>
            <thead><tr><th style={st.th}>شرح</th><th style={st.th}>بدهکار</th><th style={st.th}>بستانکار</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (r.NewPage === 1 ? (
                <tr key={i}><td colSpan={3} style={{ ...st.td, fontWeight: 700, background: '#e8eef6', whiteSpace: 'pre-line' }}>{fa(r.Remark)}</td></tr>
              ) : (
                <tr key={i}>
                  <td style={{ ...st.td, whiteSpace: 'pre-line' }}>{fa(r.Remark)}</td>
                  <td style={st.tdN}>{r.Debit == null ? '' : money(r.Debit)}</td>
                  <td style={st.tdN}>{r.Credit == null ? '' : money(r.Credit)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function SanamaDialog({ year, toDate, onClose }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [acc, setAcc] = useState('');
  const [toDoc, setToDoc] = useState('');
  const [finalAllowed, setFinalAllowed] = useState(false);
  const [finalTaraz, setFinalTaraz] = useState(false);
  const [grid, setGrid] = useState(null);
  const [sel, setSel] = useState(-1);
  const [detail, setDetail] = useState(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [control, setControl] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msgs, setMsgs] = useState([]);

  useEffect(() => {
    exportJson('/sanama/', { year, to: toDate, init: 1 })
      .then((d) => { setFrom(String(d.from || '')); setTo(String(d.to || '')); setFinalAllowed(!!d.final_taraz_allowed); })
      .catch((e) => setErr(e.message));
  }, [year, toDate]);

  const params = () => ({ year, to: toDate, acc, to_doc: toDoc });

  const job = async (label, fn) => {
    setErr(''); setMsgs([]); setBusy(label);
    try { await fn(); } catch (e) { setErr(e.message || 'خطا'); } finally { setBusy(''); }
  };

  const calculate = () => job('محاسبه', async () => {
    setDetail(null);
    const d = await exportJson('/sanama/', params());
    setGrid(d); setSel(-1); setFinalAllowed(!!d.final_taraz_allowed);
  });

  const save = () => job('ذخیره', async () => {
    if (!grid || !grid.rows.length) return;
    const name = await exportDownload('/sanama/file/', { ...params(), final: finalAllowed && finalTaraz }, 'Sanama.zip');
    setMsgs([`فایل سناما ایجاد شد: ${name}`]);
  });

  const excel = () => job('اکسل', async () => {
    if (detail) {
      await exportPostDownload('/sanama/details/', { year, to: toDate, row: detail.row, format: 'xlsx' }, 'جزئیات سناما.xlsx');
    } else {
      await exportDownload('/sanama/', { ...params(), format: 'xlsx' }, 'ایجاد فایل سناما.xlsx');
    }
  });

  const runControl = (mode) => job('کنترل', async () => {
    const d = await exportJson('/sanama/control/', { year, from, to, mode });
    setMsgs(d.messages || []);
    if (d.rows && d.rows.length) setControl({ title: d.title, rows: d.rows });
  });

  const openDetail = (r) => job('جزئیات', async () => {
    const d = await exportPost('/sanama/details/', { year, to: toDate, row: r });
    if (!d.rows.length) return;
    setDetail({ ...d, row: r }); setDetailSearch('');
  });

  const detailRows = useMemo(() => {
    if (!detail) return [];
    const s = detailSearch.trim().replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[,،]/g, '');
    if (!s || s === '0') return detail.rows;
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      return detail.rows.filter((r) => Number(r.DocumentDetailDebit || 0) + Number(r.DocumentDetailCredit || 0) === n);
    }
    return detail.rows.filter((r) => Object.values(r).some((v) => typeof v === 'string' && v.includes(detailSearch.trim())));
  }, [detail, detailSearch]);

  const dsum = useMemo(() => {
    let d = 0, c = 0;
    detailRows.forEach((r) => { d += Number(r.DocumentDetailDebit || 0); c += Number(r.DocumentDetailCredit || 0); });
    return { d, c, rd: Math.max(d - c, 0), rc: Math.max(c - d, 0) };
  }, [detailRows]);

  return (
    <div style={st.overlay}>
      <div style={st.box}>
        <div style={st.head}>
          <span>ایجاد فایل سناما — سال مالی {fa(year)} — تا تاریخ {fa(toDate)}</span>
          <button onClick={onClose} style={st.x}>✕</button>
        </div>

        <div style={st.bar}>
          <fieldset style={st.fs}>
            <legend style={st.lg}>کنترل</legend>
            <label style={st.lbl}>از سند</label>
            <input value={from} onChange={(e) => setFrom(e.target.value)} style={st.inpNum} />
            <label style={st.lbl}>تا سند</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} style={st.inpNum} />
            <button disabled={!!busy} onClick={() => runControl('range')} style={st.btn}>کنترل</button>
            <button disabled={!!busy} onClick={() => runControl('docs')} style={st.btn}>کنترل اسناد</button>
          </fieldset>
          <fieldset style={st.fs}>
            <legend style={st.lg}>محاسبه</legend>
            <label style={st.lbl}>جستجوی حساب</label>
            <input value={acc} onChange={(e) => setAcc(e.target.value)} style={st.inpNum} placeholder="کد معین" />
            <label style={st.lbl}>تا سند شماره</label>
            <input value={toDoc} onChange={(e) => setToDoc(e.target.value)} style={st.inpNum} />
            <button disabled={!!busy} onClick={calculate} style={st.btnGo}>محاسبه</button>
          </fieldset>
          <fieldset style={st.fs}>
            <legend style={st.lg}>فایل</legend>
            {finalAllowed && (
              <label style={st.chk}>
                <input type="checkbox" checked={finalTaraz} onChange={(e) => setFinalTaraz(e.target.checked)} />
                تراز نهایی
              </label>
            )}
            <button disabled={!!busy || !grid || !grid.rows.length} onClick={save} style={st.btnGo}>💾 ذخیره فایل</button>
            <button disabled={!!busy || (!grid && !detail)} onClick={excel} style={st.btnXls}>📊 اکسل</button>
          </fieldset>
        </div>

        {(busy || err || msgs.length > 0) && (
          <div style={st.msgs}>
            {busy && <div style={st.info}>در حال {busy} …</div>}
            {err && <div style={st.err}>{err}</div>}
            {msgs.map((m, i) => <div key={i} style={st.info}>{fa(m)}</div>)}
          </div>
        )}

        <div style={st.body}>
          {grid ? (
            <Grid columns={grid.columns} rows={grid.rows} selected={sel} onSelect={setSel}
              onRowContext={(r) => openDetail(r)} maxHeight="100%" />
          ) : (
            <div style={st.hint}>برای نمایش گرید سناما «محاسبه» را بزنید. راست‌کلیک روی هر ردیف، آرتیکل‌های آن ردیف را نشان می‌دهد.</div>
          )}

          {detail && (
            <div style={st.detail} onContextMenu={(e) => { e.preventDefault(); setDetail(null); }}>
              <div style={st.detailBar}>
                <b>آرتیکل‌های ردیف — حساب {fa(detail.row.AccCode)}</b>
                <input value={detailSearch} onChange={(e) => setDetailSearch(e.target.value)} style={st.inp}
                  placeholder="جستجو: مبلغ یا متن" />
                <span>تعداد: {fa(detailRows.length)}</span>
                <span>بدهکار: {money(dsum.d)}</span>
                <span>بستانکار: {money(dsum.c)}</span>
                <span>مانده بدهکار: {money(dsum.rd)}</span>
                <span>مانده بستانکار: {money(dsum.rc)}</span>
                <button onClick={() => setDetail(null)} style={st.btnSm}>بستن</button>
              </div>
              <Grid columns={detail.columns} rows={detailRows}
                onRowDouble={(r) => setMsgs([`سند ${r.DocumentNo} — تاریخ ${r.DocumentDate} — ردیف ${r.DocumentDetailPaperNo}`])}
                maxHeight="calc(100% - 46px)" />
            </div>
          )}
        </div>

        {grid && (
          <div style={st.foot}>
            <span>تعداد ردیف: <b>{fa(grid.count)}</b></span>
            <span>گردش بدهکار: <b>{money(grid.deptor)}</b></span>
            <span>گردش بستانکار: <b>{money(grid.creditor)}</b></span>
            <span>مانده: <b style={{ color: grid.remain < 0 ? '#c62828' : grid.remain > 0 ? '#1b5e20' : '#333' }}>
              {money(grid.remain)}</b></span>
          </div>
        )}
      </div>

      {control && <ControlPreview title={control.title} rows={control.rows} onClose={() => setControl(null)} />}
    </div>
  );
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1400, display: 'flex',
             alignItems: 'stretch', justifyContent: 'stretch', direction: 'rtl' },
  overlay2: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1500, display: 'flex',
              alignItems: 'center', justifyContent: 'center', direction: 'rtl' },
  box: { background: '#fff', flex: 1, margin: 8, borderRadius: 10, display: 'flex', flexDirection: 'column',
         overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,.35)' },
  preview: { background: '#fff', width: 760, maxWidth: '95vw', maxHeight: '90vh', borderRadius: 10,
             display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  head: { background: 'linear-gradient(135deg,#14233b,#1a2f50)', color: '#fff', padding: '9px 14px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: 14 },
  x: { background: 'transparent', border: 'none', color: '#fff', fontSize: 15, cursor: 'pointer', marginRight: 8 },
  bar: { display: 'flex', gap: 8, padding: '6px 10px', background: '#f3f4f6', flexWrap: 'wrap', alignItems: 'stretch' },
  fs: { border: '1px solid #cbd0d8', borderRadius: 8, padding: '4px 8px', display: 'flex', gap: 6, alignItems: 'center',
        flexWrap: 'wrap', margin: 0 },
  lg: { fontSize: 11.5, color: '#14233b', fontWeight: 700, padding: '0 4px' },
  lbl: { fontSize: 12, color: '#334' },
  chk: { display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' },
  inp: { padding: '5px 8px', border: '1px solid #cbd0d8', borderRadius: 6, fontFamily: 'inherit', fontSize: 12.5, minWidth: 160 },
  inpNum: { width: 90, padding: '5px 8px', border: '1px solid #cbd0d8', borderRadius: 6, fontFamily: 'inherit',
            fontSize: 12.5, direction: 'ltr', textAlign: 'left' },
  btn: { padding: '6px 12px', background: '#e2e5ea', border: 'none', borderRadius: 7, fontFamily: 'inherit',
         fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: '#14233b' },
  btnGo: { padding: '6px 14px', background: 'linear-gradient(135deg,#14233b,#1a2f50)', color: '#fff', border: 'none',
           borderRadius: 7, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  btnXls: { padding: '6px 12px', background: 'linear-gradient(135deg,#1b5e20,#2e7d32)', color: '#fff', border: 'none',
            borderRadius: 7, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  btnSm: { padding: '4px 10px', background: '#C8A84B', color: '#14233b', border: 'none', borderRadius: 6,
           fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  msgs: { padding: '4px 12px' },
  info: { background: '#e8f1fb', color: '#14233b', borderRadius: 6, padding: '5px 10px', margin: '3px 0', fontSize: 12.5,
          whiteSpace: 'pre-line' },
  err: { background: '#fdecea', color: '#a1201a', borderRadius: 6, padding: '5px 10px', margin: '3px 0', fontSize: 12.5 },
  body: { flex: 1, position: 'relative', overflow: 'hidden', padding: '6px 10px' },
  hint: { color: '#667', fontSize: 13, padding: 20, textAlign: 'center' },
  gridWrap: { overflow: 'auto', height: '100%', border: '1px solid #d6dae0', borderRadius: 6 },
  table: { borderCollapse: 'collapse', fontSize: 12, width: 'max-content', minWidth: '100%' },
  th: { position: 'sticky', top: 0, background: '#e8ebf0', border: '1px solid #cfd4db', padding: '4px 6px',
        whiteSpace: 'nowrap', fontWeight: 700, zIndex: 1 },
  td: { border: '1px solid #e1e4e8', padding: '3px 6px', whiteSpace: 'pre-line', verticalAlign: 'top', maxWidth: 320 },
  tdC: { border: '1px solid #e1e4e8', padding: '3px 6px', textAlign: 'center', color: '#667' },
  tdN: { border: '1px solid #e1e4e8', padding: '3px 6px', direction: 'ltr', textAlign: 'left', whiteSpace: 'nowrap' },
  warn: { color: '#b71c1c' },
  empty: { textAlign: 'center', padding: 16, color: '#888' },
  detail: { position: 'absolute', left: 10, right: 10, bottom: 6, height: '62%', background: '#fff',
            border: '2px solid #1a2f50', borderRadius: 8, boxShadow: '0 -6px 24px rgba(0,0,0,.25)', padding: 6 },
  detailBar: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, marginBottom: 6 },
  foot: { display: 'flex', gap: 22, padding: '7px 14px', background: '#f3f4f6', fontSize: 13 },
};
